/**
 * cart-note.js — fully delegated (works through re-renders & shadow roots)
 * - CC 6–10, NIT 9–10 + DV(1) + tipo_nit
 * - Saves cart attributes + note
 * - Blocks checkout until valid
 * - Hooks cart network (js & non-js endpoints)
 * - Uses event delegation so inputs can be replaced freely
 */
(function () {
  const DBG = true;
  const log  = (...a)=>{ if(DBG) console.log('[cart-note]', ...a); };
  const warn = (...a)=>{ if(DBG) console.warn('[cart-note]', ...a); };

  function localDebounce(fn, wait){let t;return(...a)=>{clearTimeout(t);t=setTimeout(()=>fn(...a),wait);};}
  const debounceFn = (typeof window.debounce === 'function') ? window.debounce : localDebounce;
  const onlyDigits = s => (s||'').replace(/\D+/g,'');
  const HOST_FLAG = '__docValidationDelegated'; // we only flag the delegated listeners now

  // --- cart update helper ---
  function cartUpdate(body){
    const url = (window.theme && theme.routes && theme.routes.cartUpdate) ? theme.routes.cartUpdate : '/cart/update.js';
    return fetch(url, {
      method:'POST',
      headers:{ Accept:'application/json','Content-Type':'application/json' },
      body: JSON.stringify(body)
    }).then(r=>r.json()).catch((e)=>{ warn('cartUpdate failed:', e); return {}; });
  }

  function buildNote({type, num, dv, tipoNit}){
    if(type==='NIT'){
      const dvStr = dv ? `-${dv}` : '';
      const tipoStr = tipoNit ? ` | Tipo NIT: ${tipoNit}` : '';
      return `Tipo documento: NIT${tipoStr} | Número: ${num}${dvStr}`;
    }
    return `Tipo documento: ${type} | Número: ${num}`;
  }

  // ---- query helpers (support id or data-attrs) ----
  function sel(root, s){ return root.querySelector(s); }
  const S = {
    type:        '#doc-type, [data-doc-type]',
    number:      '#doc-number, [data-doc-number]',
    nitDV:       '#nit-dv, [data-nit-dv]',
    nitType:     '#nit-type, [data-nit-type]',
    nitWrap:     '#nit-type-wrap, [data-nit-type-wrap]',
    errType:     '#doc-type-error, [data-doc-type-error]',
    errNumber:   '#doc-number-error, [data-doc-number-error]',
    errDV:       '#nit-dv-error, [data-nit-dv-error]',
    errNitType:  '#nit-type-error, [data-nit-type-error]',
    checkoutBtn: '[name="checkout"], button.checkout, a.checkout, a[href="/checkout"]'
  };

  // ---------- network hooks (fetch + XHR; js & non-js endpoints) ----------
  (function installCartNetworkHook(){
    if (window.__cartHookInstalled) return;
    window.__cartHookInstalled = true;

    const CART_RE = /\/cart(?:\/(?:add|change|update|clear))?(?:\.js)?(?:\?|$)/i;
    const hasSectionsParam = url => /\bsections=|section_id=/.test(url);
    const tick = ()=>requestAnimationFrame(()=>setTimeout(()=>document.dispatchEvent(new CustomEvent('cart:network-change')),0));

    const origFetch = window.fetch;
    window.fetch = function(input, init){
      const url = (typeof input === 'string') ? input : (input && input.url) || '';
      const hit = url && (CART_RE.test(url) || hasSectionsParam(url));
      const p = origFetch.apply(this, arguments);
      if (hit) p.finally(tick);
      return p;
    };

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url){
      this.__cartURL = (typeof url === 'string') ? url : (url && url.toString()) || '';
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function(){
      const url = this.__cartURL || '';
      const hit = url && (CART_RE.test(url) || hasSectionsParam(url));
      if (hit) this.addEventListener('loadend', tick, { once:true });
      return origSend.apply(this, arguments);
    };

    log('network hooks installed');
  })();

  // ---------- delegated wiring ----------
  function getHostRoots(){
    // document + any open shadow roots commonly used by Shopify themes
    const roots = [document];
    document.querySelectorAll('*').forEach(el=>{ if(el.shadowRoot) roots.push(el.shadowRoot); });
    return [...new Set(roots)];
  }

  function setError(el, errEl, msg){
    if(!errEl || !el) return;
    if(msg){ el.setAttribute('aria-invalid','true'); errEl.textContent=msg; errEl.style.display='block'; }
    else   { el.removeAttribute('aria-invalid'); errEl.textContent=''; errEl.style.display='none'; }
  }

  function toggleNitUI(root){
    const typeEl = sel(root, S.type);
    const nitDV  = sel(root, S.nitDV);
    const wrap   = sel(root, S.nitWrap);
    const isNIT = !!typeEl && (typeEl.value === 'NIT');
    if(nitDV){ nitDV.style.display = isNIT ? '' : 'none'; if(!isNIT){ nitDV.value=''; } }
    if(wrap){  wrap.style.display  = isNIT ? '' : 'none'; }
  }

  function validate(root){
    const typeEl      = sel(root, S.type);
    const numberEl    = sel(root, S.number);
    const nitDVEl     = sel(root, S.nitDV);
    const nitTypeEl   = sel(root, S.nitType);
    const errTypeEl   = sel(root, S.errType);
    const errNumberEl = sel(root, S.errNumber);
    const errDVErr    = sel(root, S.errDV);
    const errNitErr   = sel(root, S.errNitType);

    if(!typeEl || !numberEl){
      // If inputs are not present in this root, there's nothing to validate
      return { ok: true, type:'', num:'', dv:'', tipoNit:'' };
    }

    let ok = true;

    setError(nitDVEl || numberEl, errDVErr, '');
    setError(nitTypeEl || numberEl, errNitErr, '');

    const type = (typeEl.value || '').trim();
    let num = onlyDigits(numberEl.value);
    if (numberEl.value !== num) numberEl.value = num;

    if (!type) { setError(typeEl, errTypeEl, 'Selecciona el tipo de documento.'); ok = false; }
    else { setError(typeEl, errTypeEl, ''); }

    let numberValid = false;
    if (!num) {
      setError(numberEl, errNumberEl, 'Ingresa el número de identificación.');
    } else if (type === 'NIT' && (num.length < 9 || num.length > 10)) {
      setError(numberEl, errNumberEl, 'NIT debe tener 9 o 10 dígitos.');
    } else if (type === 'CC' && (num.length < 6 || num.length > 10)) {
      setError(numberEl, errNumberEl, 'CC debe tener entre 6 y 10 dígitos.');
    } else if (type !== 'NIT' && type !== 'CC') {
      setError(numberEl, errNumberEl, 'Tipo de documento inválido.');
    } else {
      setError(numberEl, errNumberEl, '');
      numberValid = true;
    }
    ok = ok && numberValid;

    let dv = '';
    let tipoNit = '';
    if (type === 'NIT' && numberValid) {
      dv = onlyDigits(nitDVEl ? nitDVEl.value : '');
      if (nitDVEl && nitDVEl.value !== dv) nitDVEl.value = dv;

      if (!dv || dv.length !== 1) { setError(nitDVEl || numberEl, sel(root, S.errDV), 'DV debe ser 1 dígito.'); ok = false; }
      else { setError(nitDVEl || numberEl, sel(root, S.errDV), ''); }

      tipoNit = (nitTypeEl && nitTypeEl.value) ? nitTypeEl.value.trim() : '';
      if (!tipoNit) { setError(nitTypeEl || numberEl, sel(root, S.errNitType), 'Selecciona el tipo de NIT.'); ok = false; }
      else { setError(nitTypeEl || numberEl, sel(root, S.errNitType), ''); }
    }

    return { ok, type, num, dv, tipoNit };
  }

  function saveAll(root){
    const v = validate(root);
    if(!v.ok) return Promise.reject(new Error('invalid'));
    const note = buildNote(v);
    const attrs = {
      'tipo_documento': v.type,
      'numero_identificacion': v.num,
      'dv_nit':  v.type === 'NIT' ? v.dv : '',
      'tipo_nit':v.type === 'NIT' ? v.tipoNit : ''
    };
    return cartUpdate({ attributes: attrs, note });
  }

  const saveAllDebounced = debounceFn((root)=>saveAll(root), 250);

  function delegateOn(root){
    const host = root === document ? document : root; // add to the root itself
    if (host[HOST_FLAG]) return; // already has delegated listeners
    host[HOST_FLAG] = true;

    // INPUT / CHANGE delegation
    host.addEventListener('input', (e)=>{
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest(S.number) || t.closest(S.nitDV)) {
        saveAllDebounced(root);
      }
    }, true); // capture to catch within shadow roots early

    host.addEventListener('change', (e)=>{
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest(S.type)) {
        toggleNitUI(root);
        saveAllDebounced(root);
      } else if (t.closest(S.nitType)) {
        saveAllDebounced(root);
      }
    }, true);

    // CHECKOUT click delegation
    host.addEventListener('click', (e)=>{
      const btn = e.target && e.target.closest && e.target.closest(S.checkoutBtn);
      if(!btn) return;

      // If inputs aren’t present in this view, do nothing special
      const typePresent = sel(root, S.type);
      const numPresent  = sel(root, S.number);
      if (!typePresent || !numPresent) return;

      const { ok } = validate(root);
      if(!ok){ e.preventDefault(); return; }
      e.preventDefault();
      saveAll(root).then(()=>{ window.location.href='/checkout'; })
                   .catch(()=>{ window.location.href='/checkout'; });
    }, true);

    log('delegated listeners attached to', root === document ? 'document' : 'shadow-root');
  }

  // Attach delegation once to document and all open shadow roots
  function ensureDelegation(){
    getHostRoots().forEach(root=>{
      try { delegateOn(root); } catch(e){ warn('delegate error', e); }
    });
  }

  // Try to set initial UI state when inputs exist
  function touchUI(){
    getHostRoots().forEach(root=>{
      if (sel(root, S.type)) toggleNitUI(root);
    });
  }

  // Initial setup
  log('boot');
  ensureDelegation();
  touchUI();

  // Re-run UI pass after cart network changes or section loads
  const reinit = localDebounce(()=>{ log('reinit tick'); ensureDelegation(); touchUI(); }, 50);

  document.addEventListener('cart:network-change', reinit);
  document.addEventListener('cart:open', reinit);
  document.addEventListener('cart:updated', reinit);
  document.addEventListener('cart:rendered', reinit);
  document.addEventListener('shopify:section:load', reinit);
  document.addEventListener('shopify:section:select', reinit);
  document.addEventListener('shopify:section:deselect', reinit);

  // Also observe DOM to catch brand-new open shadow roots
  const mo = new MutationObserver(()=>reinit());
  mo.observe(document.documentElement, { childList:true, subtree:true });

  // ---- expose global helpers for other scripts (popup, etc.) ----
  window.cartDoc = window.cartDoc || {};

  // returns { ok, ... } and paints errors in UI
  window.cartDoc.validate = function () {
    // prefer the view where the inputs actually exist
    const roots = getHostRoots();
    for (const r of roots) {
      if (sel(r, S.type) && sel(r, S.number)) return validate(r);
    }
    return { ok: true };
  };

})();
